import { useState } from 'react';
import styles from './Modal.module.css';

const TITLE_MAX = 100;
const URL_MAX = 455;

function normalizeUrl(value) {
  const trimmed = value.trim();
  if (!trimmed) return trimmed;
  if (/^https?:\/\//i.test(trimmed)) return trimmed;
  return `https://${trimmed}`;
}

function isValidUrl(value) {
  try {
    const parsed = new URL(value);
    return parsed.protocol === 'http:' || parsed.protocol === 'https:';
  } catch {
    return false;
  }
}

function validateForm({ title, url, description }) {
  const trimmedTitle = title.trim();
  const normalizedUrl = normalizeUrl(url);
  const trimmedDescription = description.trim();

  if (!trimmedTitle) return 'Title is required';
  if (trimmedTitle.length > TITLE_MAX) {
    return `Title must be ${TITLE_MAX} characters or fewer`;
  }
  if (!normalizedUrl) return 'URL is required';
  if (normalizedUrl.length > URL_MAX) {
    return `URL must be ${URL_MAX} characters or fewer`;
  }
  if (!isValidUrl(normalizedUrl)) {
    return 'URL must be a valid http or https address';
  }
  if (!trimmedDescription) return 'Description is required';

  return '';
}

function buildPayload({ title, url, description }) {
  return {
    title: title.trim(),
    url: normalizeUrl(url),
    description: description.trim(),
  };
}

export default function AddDashboardUrlModal({ onClose, onSave }) {
  const [title, setTitle] = useState('');
  const [url, setUrl] = useState('');
  const [description, setDescription] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const handleSave = async () => {
    const payload = buildPayload({ title, url, description });
    const validationError = validateForm({ title, url, description });
    if (validationError) {
      setError(validationError);
      return;
    }

    setError('');
    setSaving(true);
    try {
      await onSave(payload);
    } catch (e) {
      setError(e.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className={styles.overlay} onClick={onClose}>
      <div className={styles.panel} onClick={(e) => e.stopPropagation()}>
        <div className={styles.header}>
          <h3 className={styles.title}>Add Custom URL</h3>
          <button type="button" className={styles.closeBtn} onClick={onClose}>
            ✕
          </button>
        </div>

        <div className={styles.body}>
          <div className={styles.field}>
            <label className={styles.fieldLabel}>
              Title<span style={{ color: '#ef4444' }}> *</span>
            </label>
            <input
              className={styles.fieldInput}
              type="text"
              value={title}
              maxLength={TITLE_MAX}
              onChange={(e) => setTitle(e.target.value)}
              placeholder="Analytics"
            />
          </div>

          <div className={styles.field}>
            <label className={styles.fieldLabel}>
              URL<span style={{ color: '#ef4444' }}> *</span>
            </label>
            <input
              className={styles.fieldInput}
              type="text"
              value={url}
              maxLength={URL_MAX}
              onChange={(e) => setUrl(e.target.value)}
              placeholder="https://example.com/analytics"
            />
          </div>

          <div className={styles.field}>
            <label className={styles.fieldLabel}>
              Description<span style={{ color: '#ef4444' }}> *</span>
            </label>
            <textarea
              className={styles.fieldInput}
              style={{ minHeight: 80, resize: 'vertical' }}
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Main analytics dashboard"
            />
          </div>

          {error && <div className={styles.inlineError}>⚠ {error}</div>}
        </div>

        <div className={styles.footer}>
          <button type="button" className={styles.cancelBtn} onClick={onClose}>
            Cancel
          </button>
          <button
            type="button"
            className={styles.saveBtn}
            onClick={handleSave}
            disabled={saving}
          >
            {saving ? 'Adding…' : '＋ Add URL'}
          </button>
        </div>
      </div>
    </div>
  );
}
